/**
 * Composable для управления качеством видео
 *
 * Отвечает за:
 * - Определение возможностей устройства
 * - Применение профилей качества к видео потоку
 * - Адаптивное изменение качества на основе статистики
 * - Безопасное переключение качества
 */

import { ref, type Ref } from 'vue';
import type {
  QualityLevel,
  QualityProfile,
  DeviceCapabilities,
  ConnectionStats
} from '../types';
import { QUALITY_PROFILES, MIN_CPU_CORES, MIN_DEVICE_WIDTH, MIN_DEVICE_FRAMERATE, LOW_END_SUPPORTED_QUALITIES, QUALITY_DOWNGRADE_THRESHOLDS, QUALITY_UPGRADE_THRESHOLDS, CONSECUTIVE_FAILURES_THRESHOLD, QUALITY_ADAPTATION_WARMUP } from '../constants';
import { applyQualityToTrack, getVideoCapabilities, getVideoSettings } from '../utils/mediaTracks';
import { isMobileDevice } from '../utils/cameraDetection';

export function useQualityControl() {
  // ==================== СОСТОЯНИЕ ====================

  /** Текущее качество видео */
  const currentQuality: Ref<QualityLevel> = ref('480p');

  /** Возможности устройства */
  const deviceCapabilities: Ref<DeviceCapabilities> = ref({
    maxWidth: 1920,
    maxHeight: 1080,
    maxFrameRate: 30,
    supportedQualities: ['180p', '360p', '480p', '720p', '1080p'],
    isLowEndDevice: false,
    isMobile: false
  });

  /** Профили качества */
  const qualityProfiles: Record<QualityLevel, QualityProfile> = QUALITY_PROFILES;

  /** Счетчик последовательных неудач (для понижения качества) */
  let consecutiveFailures = 0;

  /** Счетчик проверок (для warmup перед адаптацией) */
  let checksCount = 0;

  // ==================== ОПРЕДЕЛЕНИЕ ВОЗМОЖНОСТЕЙ ====================

  /**
   * Определяет возможности устройства
   */
  async function detectDeviceCapabilities(stream: MediaStream | null): Promise<void> {
    try {
      console.log('🔍 Определение возможностей устройства...');

      // Проверяем мобильное устройство
      const isMobile = isMobileDevice();
      deviceCapabilities.value.isMobile = isMobile;

      if (!stream) {
        console.warn('⚠️ Нет потока для определения возможностей');
        return;
      }

      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        console.warn('⚠️ Нет видео трека');
        return;
      }

      // Получаем capabilities через утилиту
      const capabilities = getVideoCapabilities(videoTrack);
      const settings = getVideoSettings(videoTrack);

      console.log('📊 Capabilities:', capabilities);
      console.log('⚙️ Settings:', settings);

      // Определяем максимальные значения
      if (capabilities?.width) {
        deviceCapabilities.value.maxWidth = capabilities.width.max || 1920;
      }
      if (capabilities?.height) {
        deviceCapabilities.value.maxHeight = capabilities.height.max || 1080;
      }
      if (capabilities?.frameRate) {
        deviceCapabilities.value.maxFrameRate = capabilities.frameRate.max || 30;
      }

      // Определяем поддерживаемые качества
      const maxWidth = deviceCapabilities.value.maxWidth;
      const maxHeight = deviceCapabilities.value.maxHeight;
      const maxFrameRate = deviceCapabilities.value.maxFrameRate;
      const cpuCores = navigator.hardwareConcurrency || 2;

      // Критерии слабого устройства
      const isLowEnd = cpuCores < MIN_CPU_CORES || maxWidth < MIN_DEVICE_WIDTH || maxFrameRate < MIN_DEVICE_FRAMERATE;
      deviceCapabilities.value.isLowEndDevice = isLowEnd;

      // Фильтруем поддерживаемые качества
      const supportedQualities: QualityLevel[] = [];

      for (const [quality, profile] of Object.entries(qualityProfiles)) {
        if (profile.width <= maxWidth && profile.height <= maxHeight) {
          supportedQualities.push(quality as QualityLevel);
        }
      }

      // Для слабых устройств ограничиваем качество
      if (isLowEnd) {
        deviceCapabilities.value.supportedQualities = supportedQualities.filter(q =>
          LOW_END_SUPPORTED_QUALITIES.includes(q as any)
        );
      } else {
        deviceCapabilities.value.supportedQualities = supportedQualities;
      }

      // Устанавливаем начальное качество
      if (isLowEnd) {
        currentQuality.value = '360p';
      } else if (isMobile) {
        currentQuality.value = '480p';
      } else {
        currentQuality.value = '480p';
      }

      console.log('✅ Возможности устройства:', {
        maxResolution: `${deviceCapabilities.value.maxWidth}x${deviceCapabilities.value.maxHeight}`,
        maxFPS: deviceCapabilities.value.maxFrameRate,
        supportedQualities: deviceCapabilities.value.supportedQualities,
        isLowEnd,
        isMobile,
        defaultQuality: currentQuality.value
      });

    } catch (error) {
      console.error('❌ Ошибка определения возможностей устройства:', error);
    }
  }

  /**
   * Проверяет, поддерживается ли качество устройством
   */
  function isQualitySupported(quality: QualityLevel): boolean {
    return deviceCapabilities.value.supportedQualities.includes(quality);
  }

  // ==================== ПРИМЕНЕНИЕ КАЧЕСТВА ====================

  /**
   * Применяет профиль качества к видео треку
   */
  async function applyQualityToVideoTrack(
    track: MediaStreamTrack,
    quality: QualityLevel
  ): Promise<boolean> {
    try {
      const profile = qualityProfiles[quality];
      console.log(`🎥 Применение качества ${quality}:`, profile);

      const success = await applyQualityToTrack(track, profile);
      if (success) {
        console.log(`✅ Качество ${quality} применено успешно`);
      } else {
        console.error(`❌ Не удалось применить качество ${quality}`);
      }
      return success;
    } catch (error) {
      console.error(`❌ Ошибка применения качества ${quality}:`, error);
      return false;
    }
  }

  /**
   * Безопасное применение качества (с проверкой поддержки)
   */
  async function applyQualitySafely(
    stream: MediaStream | null,
    quality: QualityLevel
  ): Promise<boolean> {
    if (!stream) {
      console.warn('⚠️ Нет потока для применения качества');
      return false;
    }

    // Проверяем поддержку
    if (!isQualitySupported(quality)) {
      console.warn(`⚠️ Качество ${quality} не поддерживается устройством`);
      return false;
    }

    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) {
      console.warn('⚠️ Нет видео трека');
      return false;
    }

    const success = await applyQualityToVideoTrack(videoTrack, quality);
    if (success) {
      currentQuality.value = quality;
    }

    return success;
  }

  // ==================== АДАПТИВНОЕ КАЧЕСТВО ====================

  /**
   * Автоматическая адаптация качества на основе статистики
   */
  function adaptQuality(stats: ConnectionStats): void {
    checksCount++;

    // Warmup: пропускаем первые N проверок
    if (checksCount < QUALITY_ADAPTATION_WARMUP) {
      return;
    }

    const currentQualityIndex = deviceCapabilities.value.supportedQualities.indexOf(currentQuality.value);
    if (currentQualityIndex === -1) return;

    const isLowEnd = deviceCapabilities.value.isLowEndDevice;

    // Пороги для слабых и обычных устройств
    const thresholds = isLowEnd 
      ? QUALITY_DOWNGRADE_THRESHOLDS.lowEnd 
      : QUALITY_DOWNGRADE_THRESHOLDS.normal;

    const upgradeThresholds = isLowEnd
      ? QUALITY_UPGRADE_THRESHOLDS.lowEnd
      : QUALITY_UPGRADE_THRESHOLDS.normal;

    // Проверяем условия для понижения качества
    if (
      stats.rtt > thresholds.rtt ||
      stats.packetLoss > thresholds.packetLoss
    ) {
      consecutiveFailures++;

      if (consecutiveFailures >= CONSECUTIVE_FAILURES_THRESHOLD && currentQualityIndex > 0) {
        const lowerQuality = deviceCapabilities.value.supportedQualities[currentQualityIndex - 1];
        console.log(`📉 Понижаем качество: ${currentQuality.value} → ${lowerQuality}`);
        currentQuality.value = lowerQuality;
        consecutiveFailures = 0;
      }
    }
    // Проверяем условия для повышения качества
    else if (
      stats.rtt < upgradeThresholds.rtt &&
      stats.packetLoss < upgradeThresholds.packetLoss &&
      stats.bitrateOut > upgradeThresholds.bitrateOut
    ) {
      consecutiveFailures = 0;

      // Можем повысить качество
      if (currentQualityIndex < deviceCapabilities.value.supportedQualities.length - 1) {
        const higherQuality = deviceCapabilities.value.supportedQualities[currentQualityIndex + 1];
        console.log(`📈 Повышаем качество: ${currentQuality.value} → ${higherQuality}`);
        currentQuality.value = higherQuality;
      }
    } else {
      consecutiveFailures = 0;
    }
  }

  /**
   * Сбрасывает счетчики адаптации
   */
  function resetAdaptation(): void {
    consecutiveFailures = 0;
    checksCount = 0;
  }

  // ==================== ВОЗВРАЩАЕМЫЙ API ====================

  return {
    // Состояние
    currentQuality,
    deviceCapabilities,
    qualityProfiles,

    // Определение возможностей
    detectDeviceCapabilities,
    isQualitySupported,

    // Применение качества
    applyQualityToVideoTrack,
    applyQualitySafely,

    // Адаптивное качество
    adaptQuality,
    resetAdaptation
  };
}
